import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck, CheckCircle, XCircle, Clock, FileText, Hammer, MessageSquare, ArrowRightLeft, Search, X } from 'lucide-react'
import api from '../../core/services/api'
import { Toast } from '../../components/Toast'
import { useAuthStore } from '../../core/stores/authStore'

interface Approvazione {
  id: string
  entity_type: string
  entity_id: string
  richiedente_id: string
  richiedente_nome: string
  approvatore_id: string | null
  approvatore_nome: string | null
  livello: number
  stato: string
  note_richiesta: string | null
  note_risposta: string | null
  data_richiesta: string | null
  data_risposta: string | null
  is_mine: boolean
}

interface UserOption {
  id: string
  email: string
  first_name: string | null
  last_name: string | null
  role: string
}

export default function ApprovazioniPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const [approvazioni, setApprovazioni] = useState<Approvazione[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStato, setFilterStato] = useState('pending')
  const [filterScope, setFilterScope] = useState<'mie' | 'tutte'>('mie')
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null)

  // Note per approvazione/rifiuto
  const [noteModal, setNoteModal] = useState<{ id: string; action: 'approva' | 'rifiuta' } | null>(null)
  const [noteText, setNoteText] = useState('')
  const [processing, setProcessing] = useState(false)

  // Cessione approvazione
  const [cediModal, setCediModal] = useState<string | null>(null)
  const [cediUsers, setCediUsers] = useState<UserOption[]>([])
  const [cediSearch, setCediSearch] = useState('')
  const [cediSelectedId, setCediSelectedId] = useState<string | null>(null)
  const [cediLoading, setCediLoading] = useState(false)

  useEffect(() => {
    loadApprovazioni()
  }, [filterStato, filterScope])

  const loadApprovazioni = async () => {
    try {
      setLoading(true)
      const res = await api.get(`/v1/approvazioni?stato=${filterStato}&scope=${filterScope}`)
      setApprovazioni(res.data)
    } catch (err) {
      console.error('Errore caricamento approvazioni:', err)
      setToast({ message: 'Errore nel caricamento', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async () => {
    if (!noteModal) return

    try {
      setProcessing(true)
      await api.post(`/v1/approvazioni/${noteModal.id}/${noteModal.action}`, {
        note: noteText || null
      })
      setToast({
        message: noteModal.action === 'approva' ? 'Approvazione concessa' : 'Approvazione rifiutata',
        type: 'success'
      })
      setNoteModal(null)
      setNoteText('')
      loadApprovazioni()
    } catch (err: any) {
      setToast({
        message: err?.response?.data?.detail || 'Errore',
        type: 'error'
      })
    } finally {
      setProcessing(false)
    }
  }

  const openCediModal = async (approvazioneId: string) => {
    setCediModal(approvazioneId)
    setCediSearch('')
    setCediSelectedId(null)
    try {
      setCediLoading(true)
      const res = await api.get('/v1/anagrafiche/utenti')
      setCediUsers(res.data)
    } catch {
      setToast({ message: 'Errore caricamento utenti', type: 'error' })
    } finally {
      setCediLoading(false)
    }
  }

  const handleCedi = async () => {
    if (!cediModal || !cediSelectedId) return
    try {
      setProcessing(true)
      await api.post(`/v1/approvazioni/${cediModal}/cedi`, {
        nuovo_approvatore_id: cediSelectedId
      })
      setToast({ message: 'Approvazione ceduta', type: 'success' })
      setCediModal(null)
      loadApprovazioni()
    } catch (err: any) {
      setToast({ message: err?.response?.data?.detail || 'Errore', type: 'error' })
    } finally {
      setProcessing(false)
    }
  }

  const navigateToEntity = (entityType: string, entityId: string) => {
    if (entityType === 'offerta') {
      navigate(`/app/offerte/${entityId}`)
    } else if (entityType === 'commessa') {
      navigate(`/app/commesse/${entityId}`)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }

  const entityIcon = (type: string) => {
    if (type === 'offerta') return <FileText className="w-4 h-4" />
    if (type === 'commessa') return <Hammer className="w-4 h-4" />
    return <FileText className="w-4 h-4" />
  }

  const statoIcon = (stato: string) => {
    if (stato === 'pending') return <Clock className="w-4 h-4 text-amber-500" />
    if (stato === 'approved') return <CheckCircle className="w-4 h-4 text-green-500" />
    if (stato === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />
    return <Clock className="w-4 h-4" />
  }

  const isAdmin = user?.role === 'admin'

  const filteredCediUsers = cediUsers.filter(u => {
    const q = cediSearch.toLowerCase()
    return u.email.toLowerCase().includes(q) ||
      (u.first_name?.toLowerCase() || '').includes(q) ||
      (u.last_name?.toLowerCase() || '').includes(q)
  })

  return (
    <div>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Modale Note (approva/rifiuta) */}
      {noteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              {noteModal.action === 'approva' ? 'Approva Richiesta' : 'Rifiuta Richiesta'}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {noteModal.action === 'approva'
                ? 'Puoi aggiungere una nota opzionale.'
                : 'Aggiungi una motivazione per il rifiuto.'}
            </p>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Note (opzionale)..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-4"
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setNoteModal(null); setNoteText('') }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={handleAction}
                disabled={processing}
                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                  noteModal.action === 'approva'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {processing ? 'Elaborazione...' : noteModal.action === 'approva' ? 'Approva' : 'Rifiuta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modale Cedi Approvazione */}
      {cediModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cedi Approvazione</h2>
                <p className="text-sm text-gray-500">Riassegna a un altro utente</p>
              </div>
              <button onClick={() => setCediModal(null)} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca utente..."
                  value={cediSearch}
                  onChange={(e) => setCediSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
              </div>
              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {cediLoading ? (
                  <div className="p-4 text-center text-gray-500 text-sm">Caricamento...</div>
                ) : filteredCediUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">Nessun utente</div>
                ) : (
                  filteredCediUsers.map(u => (
                    <button
                      key={u.id}
                      onClick={() => setCediSelectedId(u.id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors ${
                        cediSelectedId === u.id ? 'bg-indigo-50 border-l-2 border-indigo-500' : ''
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">{u.first_name} {u.last_name}</div>
                      <div className="text-xs text-gray-500">{u.email}</div>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200">
              <button onClick={() => setCediModal(null)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg text-sm">
                Annulla
              </button>
              <button
                onClick={handleCedi}
                disabled={!cediSelectedId || processing}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm"
              >
                <ArrowRightLeft className="w-4 h-4" />
                {processing ? 'Cedendo...' : 'Cedi'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Approvazioni</h1>
          <p className="text-gray-500">Gestione richieste di approvazione</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            {['pending', 'approved', 'rejected'].map((stato) => (
              <button
                key={stato}
                onClick={() => setFilterStato(stato)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filterStato === stato
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {stato === 'pending' ? 'In Attesa' : stato === 'approved' ? 'Approvate' : 'Rifiutate'}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterScope('mie')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filterScope === 'mie'
                  ? 'bg-indigo-100 text-indigo-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Le mie
            </button>
            <button
              onClick={() => setFilterScope('tutte')}
              className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filterScope === 'tutte'
                  ? 'bg-indigo-100 text-indigo-700 font-medium'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              Tutte
            </button>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Caricamento...</div>
        ) : approvazioni.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <ShieldCheck className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            Nessuna approvazione {filterStato === 'pending' ? 'in attesa' : filterStato === 'approved' ? 'approvata' : 'rifiutata'}
            {filterScope === 'mie' && ' per te'}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {approvazioni.map((app) => (
              <div key={app.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {statoIcon(app.stato)}
                    <div>
                      <div className="flex items-center gap-2">
                        {entityIcon(app.entity_type)}
                        <button
                          onClick={() => navigateToEntity(app.entity_type, app.entity_id)}
                          className="font-medium text-primary-600 hover:underline"
                        >
                          {app.entity_type === 'offerta' ? 'Offerta' : 'Commessa'}
                        </button>
                        <span className="text-xs text-gray-400 font-mono">{app.entity_id.slice(0, 8)}...</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        Richiesta da <span className="font-medium">{app.richiedente_nome}</span>
                        {' '}&middot;{' '}{formatDate(app.data_richiesta)}
                      </div>
                      {app.approvatore_nome && app.stato === 'pending' && (
                        <div className="text-sm text-gray-500">
                          Assegnata a <span className="font-medium">{app.approvatore_nome}</span>
                          {app.is_mine && <span className="text-indigo-600 ml-1">(tu)</span>}
                        </div>
                      )}
                      {app.note_richiesta && (
                        <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                          <MessageSquare className="w-3 h-3" />
                          {app.note_richiesta}
                        </div>
                      )}
                      {app.stato !== 'pending' && app.approvatore_nome && (
                        <div className="text-sm text-gray-500 mt-1">
                          {app.stato === 'approved' ? 'Approvata' : 'Rifiutata'} da{' '}
                          <span className="font-medium">{app.approvatore_nome}</span>
                          {' '}&middot;{' '}{formatDate(app.data_risposta)}
                          {app.note_risposta && (
                            <span className="text-gray-400"> — {app.note_risposta}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Azioni */}
                  {app.stato === 'pending' && (
                    <div className="flex items-center gap-2">
                      {/* Approva/Rifiuta solo se e' la MIA */}
                      {app.is_mine && (
                        <>
                          <button
                            onClick={() => setNoteModal({ id: app.id, action: 'approva' })}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 text-sm font-medium"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approva
                          </button>
                          <button
                            onClick={() => setNoteModal({ id: app.id, action: 'rifiuta' })}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 text-sm font-medium"
                          >
                            <XCircle className="w-4 h-4" />
                            Rifiuta
                          </button>
                        </>
                      )}
                      {/* Cedi: solo admin */}
                      {isAdmin && (
                        <button
                          onClick={() => openCediModal(app.id)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 text-sm"
                          title="Cedi approvazione"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                          Cedi
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
